import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../Context/AuthContext";

const AllReferall = ({ activeTab }) => {
  const [allreferral, setAllreferral] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [medium, setMedium] = useState("");
  const [campaign, setCampaign] = useState("");
  const [program, setProgram] = useState("");
  const [status, setStatus] = useState("");
  const [email, setEmail] = useState("");
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [showFilters, setShowFilters] = useState(true);
  const [copiedEmail, setCopiedEmail] = useState("");
  const { token, user } = useAuth();
  const isAdmin = user?.user_type === "Admin";
  const [proxyEditTarget, setProxyEditTarget] = useState(null);
  const [proxyOptions, setProxyOptions] = useState([]);
  const [proxyLoading, setProxyLoading] = useState(false);
  const [proxySearch, setProxySearch] = useState("");
  const [selectedProxyId, setSelectedProxyId] = useState(null);
  const [savingProxy, setSavingProxy] = useState(false);
  const [proxyError, setProxyError] = useState("");

  const getallReferral = async () => {
    try {
      const resp = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/referrals/all-data`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (resp.status === 200) {
        const filteredData = resp.data.referral.map((referral) => ({
          id: referral.id,
          Referee_name: referral.candidate_name,
          Referee_email: referral.candidate_email,
          Referee_Mobile: referral.candidate_phone,
          Referee_program: referral.candidate_program,
          Referee_country: referral.candidate_country,
          Campaign: referral.campaign,
          Source: referral.utm_source,
          Medium: referral.medium,
          Status: referral.status,
          Referrer_name: referral.referrer?.first_name + (referral.referrer?.last_name ? ` ${referral.referrer.last_name}` : ''),
          Referrer_email: referral.referrer?.email,
          Proxy_Referrer_name: referral.proxy_referrer ? (referral.proxy_referrer.first_name + (referral.proxy_referrer.last_name ? ` ${referral.proxy_referrer.last_name}` : '')) : 'N/A',
          Proxy_Referrer_email: referral.proxy_referrer?.email || 'N/A',
          Added_On: formatDate(referral?.created_at),
          Updated_On: formatDate(referral?.updated_at)
        }));
        downloadCSV(filteredData);
      }
    } catch (error) {
      console.log("Error fetching referrals:", error);
    }
  };

  const convertToCSV = (data) => {
    const headers = [
      'id', 'Referee_name', 'Referee_email', 'Referee_Mobile', 'Referee_program',
      'Referee_country', 'Campaign', 'Source', 'Medium', 'Status',
      'Referrer_name', 'Referrer_email', 'Proxy_Referrer_name', 'Proxy_Referrer_email', 'Added_On', 'Updated_On'
    ];
    const csvRows = [
      headers.join(','),
      ...data.map((row) =>
        headers.map((header) => {
          const value = row[header] || '';
          return `"${value.toString().replace(/"/g, '""')}"`;
        }).join(',')
      ),
    ];
    return csvRows.join('\n');
  };

  const downloadCSV = (data) => {
    const csvData = convertToCSV(data);
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'referrals.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const AllReferallData = useCallback(async (pageOverride) => {
    try {
      const params = new URLSearchParams();

      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (medium) params.append("medium", medium);
      if (campaign) params.append("campaign", campaign);
      if (program) params.append("program", program);
      if (status) params.append("status", status.toLowerCase());
      if (email) params.append("email", email);

      const pageToFetch = pageOverride || pagination.page || 1;
      params.append("page", pageToFetch);
      params.append("limit", 10);

      const queryString = params.toString() ? `?${params.toString()}` : "";

      const resp = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/referrals${queryString}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (resp.status === 200) {
        setAllreferral(resp.data.referrals);
        setPagination(resp.data.pagination);
      }
    } catch (error) {
      console.log("Error fetching referrals:", error);
    }
    // Added pagination.page to dependencies to fix "stale closure" issue
  }, [token, startDate, endDate, medium, campaign, program, status, email, pagination.page]);

  useEffect(() => {
    AllReferallData();
  }, [AllReferallData]);

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    if (name === "startDate") setStartDate(value);
    if (name === "endDate") setEndDate(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "medium") setMedium(value);
    if (name === "campaign") setCampaign(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleProgramChange = (e) => {
    const val = e.target.value;
    setProgram(val);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleStatusChange = (e) => {
    const val = e.target.value;
    setStatus(val);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setEmail(val);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    AllReferallData();
  };

  const clearData = () => {
    setStartDate("");
    setEndDate("");
    setMedium("");
    setCampaign("");
    setProgram("");
    setStatus("");
    setEmail("");
    setPagination({ page: 1, totalPages: 1 });

    // Explicitly fetch page 1 with cleared filters
    AllReferallData(1);
  };

  const handleNextPage = () => {
    if ((pagination.page || 1) < (pagination.totalPages || 1)) {
      setPagination((prev) => ({ ...prev, page: (prev.page || 1) + 1 }));
    }
  };

  const handlePrevPage = () => {
    if ((pagination.page || 1) > 1) {
      setPagination((prev) => ({ ...prev, page: (prev.page || 1) - 1 }));
    }
  };

  const handleCopyEmail = (email) => {
    if (!email) return;
    navigator.clipboard.writeText(email);
    setCopiedEmail(email);
    setTimeout(() => setCopiedEmail(""), 2000);
  };

  const openProxyEditor = async (referral) => {
    setProxyEditTarget(referral);
    setSelectedProxyId(referral?.proxy_referrer?.id ?? null);
    setProxySearch("");
    setProxyError("");
    setProxyLoading(true);
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/team-details`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const list = resp.data?.data || resp.data?.team || resp.data || [];
      setProxyOptions(Array.isArray(list) ? list : []);
    } catch (err) {
      setProxyError(err?.response?.data?.message || "Failed to load users");
      setProxyOptions([]);
    } finally {
      setProxyLoading(false);
    }
  };

  const closeProxyEditor = () => {
    if (savingProxy) return;
    setProxyEditTarget(null);
    setProxySearch("");
    setSelectedProxyId(null);
    setProxyError("");
  };

  const handleSaveProxy = async () => {
    if (!proxyEditTarget) return;
    setSavingProxy(true);
    setProxyError("");
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_BASE_URL}/api/referrals/${proxyEditTarget.id}/proxy-referrer`,
        { proxy_referrer_id: selectedProxyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProxyEditTarget(null);
      setProxySearch("");
      setSelectedProxyId(null);
      AllReferallData();
    } catch (err) {
      setProxyError(err?.response?.data?.message || "Failed to update proxy referrer");
    } finally {
      setSavingProxy(false);
    }
  };

  const filteredProxyOptions = proxyOptions
    .filter((u) => (u.status || "").toLowerCase() === "active")
    .filter((u) => {
      if (!proxySearch) return true;
      const q = proxySearch.toLowerCase();
      return (
        (u.first_name || "").toLowerCase().includes(q) ||
        (u.last_name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.user_type || "").toLowerCase().includes(q)
      );
    });

  function formatDate(utcTimestamp) {
    if (!utcTimestamp) return "N/A";
    const date = new Date(utcTimestamp);
    const istDate = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
    return istDate.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }

  const PaginationControls = () => (
    <div className="p-2 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
      <div className="text-sm text-gray-500">
        {pagination.totalReferral || pagination.total || pagination.totalItems ? (
          <>
            Showing <span className="font-bold text-gray-900">{(pagination.page - 1) * 10 + 1}</span> to{" "}
            <span className="font-bold text-gray-900">
              {Math.min(pagination.page * 10, pagination.totalReferral || pagination.total || pagination.totalItems)}
            </span>{" "}
            of <span className="font-bold text-gray-900">{pagination.totalReferral || pagination.total || pagination.totalItems}</span> results
          </>
        ) : (
          <>
            Page <span className="font-bold text-gray-900">{pagination.page || 1}</span> of{" "}
            <span className="font-bold text-gray-900">{pagination.totalPages || 1}</span>
          </>
        )}
      </div>
      <div className="flex items-center gap-3">
        {(pagination.totalReferral || pagination.total || pagination.totalItems) ? (
          <div className="text-xs text-gray-400 mr-2 font-medium">
            Page {pagination.page} of {pagination.totalPages}
          </div>
        ) : null}
        <button
          onClick={handlePrevPage}
          disabled={pagination.page <= 1}
          className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Prev
        </button>
        <button
          onClick={handleNextPage}
          disabled={pagination.page >= pagination.totalPages}
          className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex items-center gap-2"
        >
          Next
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );

  if (activeTab !== 2) return null;

  return (
    <div className="animate-fadeIn">

      {/* Top Filter Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8 overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div className="flex items-center gap-2">
            <div className="bg-[#004f6b] p-1.5 rounded-lg text-white">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
            </div>
            <h3 className="font-bold text-gray-800">Review & Filter Referrals</h3>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={clearData} className="text-sm font-semibold text-red-600 hover:text-red-700 transition-colors">
              Reset Filters
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium text-gray-600 transition-colors flex items-center gap-2"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
              <svg className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {showFilters && (
          <form onSubmit={handleSubmit} className="p-6 transition-all animate-fadeIn">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Email Search */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-500">Search Member</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by email..."
                    className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#004f6b]/20 focus:border-[#004f6b] outline-none transition-all"
                    value={email}
                    onChange={handleEmailChange}
                  />
                  <svg className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              {/* Date Range */}
              <div className="lg:col-span-1 space-y-1.5">
                <label className="text-xs font-medium text-gray-500">Date Range</label>
                <div className="flex items-center gap-2">
                  <input
                    type="date" name="startDate" value={startDate} onChange={handleDateChange}
                    className="w-full px-2 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:border-[#004f6b] outline-none transition-all"
                  />
                  <span className="text-gray-300">-</span>
                  <input
                    type="date" name="endDate" value={endDate} onChange={handleDateChange}
                    className="w-full px-2 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:border-[#004f6b] outline-none transition-all"
                  />
                </div>
              </div>

              {/* Campaign Info */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-500">Campaign Details</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text" placeholder="Medium" name="medium" value={medium} onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:border-[#004f6b] outline-none transition-all"
                  />
                  <input
                    type="text" placeholder="Campaign" name="campaign" value={campaign} onChange={handleInputChange}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:border-[#004f6b] outline-none transition-all"
                  />
                </div>
              </div>

              {/* Selects */}
              <div className="flex gap-4">
                <div className="flex-1 space-y-1.5">
                  <label className="text-xs font-medium text-gray-500">Program</label>
                  <select value={program} onChange={handleProgramChange} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#004f6b]">
                    <option value="">All Programs</option>
                    <option value="MBA">MBA</option>
                    <option value="MCA">MCA</option>
                    <option value="M.COM">M.COM</option>
                    <option value="MA">MA</option>
                    <option value="BBA">BBA</option>
                    <option value="BCA">BCA</option>
                    <option value="B.COM">B.COM</option>
                  </select>
                </div>
                <div className="flex-1 space-y-1.5">
                  <label className="text-xs font-medium text-gray-500">Status</label>
                  <select value={status} onChange={handleStatusChange} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#004f6b]">
                    <option value="">All Status</option>
                    <option value="inprogress">In Progress</option>
                    <option value="ENROLLED">Enrolled</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-100 flex justify-end gap-3">
              {user.user_type === 'Admin' && (
                <button
                  type="button"
                  onClick={() => getallReferral()}
                  className="px-2 py-2 bg-[#38a169] hover:bg-[#2f855a] text-white rounded-lg font-semibold text-sm transition-all shadow-md flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Export CSV
                </button>
              )}
              <button type="submit" className="px-8 py-2 bg-[#004f6b] hover:bg-[#003e54] text-white rounded-lg font-semibold text-sm transition-all shadow-md shadow-[#004f6b]/20">
                Apply Filters
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Main Content Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col min-h-[500px]">
        <PaginationControls />
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-2 py-2 text-xs font-medium text-gray-600 text-center">Sr. No</th>
                <th className="px-2 py-2 text-xs font-medium text-gray-600">Referer</th>
                <th className="px-2 py-2 text-xs font-medium text-gray-600">Referee</th>
                <th className="px-2 py-2 text-xs font-medium text-gray-600">Campaign/Medium/Source</th>
                <th className="px-2 py-2 text-xs font-medium text-gray-600">Status</th>
                <th className="px-2 py-2 text-xs font-medium text-gray-600">Proxy Referer</th>
                <th className="px-2 py-2 text-xs font-medium text-gray-600">Program</th>
                <th className="px-2 py-2 text-xs font-medium text-gray-600">Contact Number</th>
                <th className="px-2 py-2 text-xs font-medium text-gray-600">Added On</th>
                <th className="px-2 py-2 text-xs font-medium text-gray-600">Updated On</th>
                {isAdmin && (
                  <th className="px-2 py-2 text-xs font-medium text-gray-600 text-center">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {allreferral.length > 0 ? (
                allreferral.map((referral, index) => (
                  <tr key={index} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="px-2 py-2 text-sm text-gray-400 font-mono text-center">
                      {(pagination.page - 1) * 10 + index + 1}
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-gray-900 group-hover:text-[#004f6b] transition-colors">
                          {referral?.referrer?.first_name} {referral?.referrer?.last_name || ''}
                        </span>
                        <div className="flex items-center gap-2">
                          <span
                            className="text-xs text-gray-500 hover:text-[#004f6b] cursor-pointer transition-colors flex items-center gap-1 group/email"
                            onClick={() => handleCopyEmail(referral?.referrer?.email)}
                            title="Click to copy"
                          >
                            {referral?.referrer?.email}
                            <svg className={`w-3 h-3  transition-opacity ${copiedEmail === referral?.referrer?.email ? 'text-green-500 opacity-100' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              {copiedEmail === referral?.referrer?.email ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                              )}
                            </svg>
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                            {referral?.referrer?.user_type}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td className="px-2 py-2">
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-gray-800">{referral?.candidate_name}</span>
                        <div className="flex items-center gap-2">
                          <span
                            className="text-xs text-gray-500 hover:text-[#004f6b] cursor-pointer flex items-center gap-1 group/refemail"
                            onClick={() => handleCopyEmail(referral?.candidate_email)}
                            title="Click to copy"
                          >
                            {referral?.candidate_email}
                            <svg className={`w-3 h-3  transition-opacity ${copiedEmail === referral?.candidate_email ? 'text-green-500 opacity-100' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              {copiedEmail === referral?.candidate_email ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                              )}
                            </svg>
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex flex-col gap-1 text-xs">
                        <div className="flex items-center gap-1">
                          <span className="text-gray-700 font-medium">{referral?.campaign || 'Organic'}</span>
                          <span className="text-gray-400">/</span>
                          <span className="text-gray-700">{referral?.medium || 'N/A'}</span>
                          <span className="text-gray-400">/</span>
                          <span className="text-gray-700">{referral?.utm_source || 'Direct'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border
                           ${referral?.status.toLowerCase() === 'enrolled' ? 'bg-green-50 text-green-700 border-green-200 shadow-sm' :
                          referral?.status.toLowerCase() === 'rejected' ? 'bg-red-50 text-red-700 border-red-200' :
                            'bg-amber-50 text-amber-700 border-amber-200'}`}>
                        {referral?.status ? referral.status.toLowerCase() : 'UNKNOWN'}
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      {referral?.proxy_referrer ? (
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-gray-900 group-hover:text-[#004f6b] transition-colors">
                            {referral?.proxy_referrer?.first_name} {referral?.proxy_referrer?.last_name || ''}
                          </span>
                          <div className="flex items-center gap-2">
                            <span
                              className="text-xs text-gray-500 hover:text-[#004f6b] cursor-pointer transition-colors flex items-center gap-1 group/proxyemail"
                              onClick={() => handleCopyEmail(referral?.proxy_referrer?.email)}
                              title="Click to copy"
                            >
                              {referral?.proxy_referrer?.email}
                              <svg className={`w-3 h-3  transition-opacity ${copiedEmail === referral?.proxy_referrer?.email ? 'text-green-500 opacity-100' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                {copiedEmail === referral?.proxy_referrer?.email ? (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                ) : (
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                )}
                              </svg>
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 mt-1.5">
                            <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                              {referral?.proxy_referrer?.user_type}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">N/A</span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <span className="px-2 py-1  text-xs  text-gray-500">
                        {referral?.candidate_program || 'N/A'}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-sm text-gray-500">
                      {referral?.candidate_phone}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-700">{formatDate(referral?.created_at).split(',')[0]}</div>
                      <div className="text-xs text-gray-400 font-medium mt-0.5">{formatDate(referral?.created_at).split(',')[1]}</div>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-700">{formatDate(referral?.updated_at).split(',')[0]}</div>
                      <div className="text-xs text-gray-400 font-medium mt-0.5">{formatDate(referral?.updated_at).split(',')[1]}</div>
                    </td>
                    {isAdmin && (
                      <td className="px-2 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => openProxyEditor(referral)}
                          className="inline-flex items-center gap-1 px-1 py-1 text-xs font-semibold text-[#004f6b] bg-[#004f6b]/5 hover:bg-[#004f6b]/10 border border-[#004f6b]/20 rounded-lg transition-colors"
                          title="Change proxy referrer"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Change Proxy
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={isAdmin ? 11 : 10} className="px-2 py-20 text-center">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <div className="bg-gray-50 p-4 rounded-full">
                        <svg className="w-12 h-12 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                      <span className="text-gray-400 text-sm font-medium">No results found matching your current filters.</span>
                      <button onClick={clearData} className="text-[#004f6b] text-xs font-semibold hover:underline">Clear all filters</button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Improved Pagination Footer */}
        <PaginationControls />
      </div>

      {proxyEditTarget && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
            <div className="flex items-start justify-between p-5 border-b border-gray-100">
              <div>
                <h3 className="text-base text-start font-semibold text-gray-900">Change Proxy Referrer</h3>
                {/* <p className="text-xs text-gray-500 mt-1">
                  Candidate: <span className="font-medium text-gray-700">{proxyEditTarget?.candidate_name}</span>
                </p> */}
                <p className="text-xs text-start text-gray-500">
                  Current:{' '}
                  <span className="font-medium text-gray-700">
                    {proxyEditTarget?.proxy_referrer
                      ? `${proxyEditTarget.proxy_referrer.first_name || ''} ${proxyEditTarget.proxy_referrer.last_name || ''} (${proxyEditTarget.proxy_referrer.email})`
                      : 'None'}
                  </span>
                </p>
              </div>
              <button
                type="button"
                onClick={closeProxyEditor}
                disabled={savingProxy}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-5 space-y-3 overflow-hidden flex flex-col flex-1">
              <input
                type="text"
                value={proxySearch}
                onChange={(e) => setProxySearch(e.target.value)}
                placeholder="Search by name, email, or role"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#004f6b]/30 focus:border-[#004f6b]"
              />

              <div className="flex-1 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-100">
                <label className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="proxy-referrer"
                    checked={selectedProxyId === null}
                    onChange={() => setSelectedProxyId(null)}
                  />
                  <span className="text-sm text-gray-700 italic">Unassign (no proxy referrer)</span>
                </label>

                {proxyLoading ? (
                  <div className="px-3 py-4 text-center text-sm text-gray-400">Loading users…</div>
                ) : filteredProxyOptions.length === 0 ? (
                  <div className="px-3 py-4 text-center text-sm text-gray-400">No matching users</div>
                ) : (
                  filteredProxyOptions.map((u) => (
                    <label key={u.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        name="proxy-referrer"
                        checked={selectedProxyId === u.id}
                        onChange={() => setSelectedProxyId(u.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {u.first_name} {u.last_name || ''}
                        </div>
                        <div className="text-xs text-gray-500 truncate">{u.email}</div>
                      </div>
                      <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                        {u.user_type}
                      </span>
                    </label>
                  ))
                )}
              </div>

              {proxyError && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {proxyError}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-100">
              <button
                type="button"
                onClick={closeProxyEditor}
                disabled={savingProxy}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveProxy}
                disabled={savingProxy}
                className="px-4 py-2 text-sm font-semibold text-white bg-[#004f6b] hover:bg-[#003d52] rounded-lg shadow-sm disabled:opacity-50"
              >
                {savingProxy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllReferall;
